import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Repeat, Target, Users, Linkedin } from "lucide-react";
import { Seo } from "../components/Seo";
import { FadeUp, Stagger, StaggerItem } from "../components/Reveal";
import { LogoMark } from "../components/Logo";
import { STATS, FOUNDER } from "../data/content";

const VALUES = [
    { icon: TrendingUp, title: "Active operator", desc: "GrowVise is run by someone still operating businesses day to day. The advice comes from current commercial reality, not a textbook." },
    { icon: Repeat, title: "Both sides of the table", desc: "Experience as a distributor and as a brand owner means buyer conversations, terms and trade spend are handled with both perspectives in mind." },
    { icon: Target, title: "Commercially accountable", desc: "Engagements are built around measured outcomes — margin points, doors opened, revenue shifted — not activity." },
    { icon: Users, title: "Owner-independent", desc: "The goal is a business that performs without the owner in every decision: clear roles, real accountability and execution rhythm." },
];

export default function About() {
    return (
        <div data-testid="about-page">
            <Seo
                title="About | GrowVise"
                description="GrowVise is a commercial advisory and implementation partner built by an active NZ operator — 16+ years across retail distribution, market entry and wholesale pricing."
                path="/about"
            />

            {/* Header — navy with brand panel */}
            <section className="bg-navy-950 pt-32 sm:pt-40 pb-16 sm:pb-20" data-testid="about-hero">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                    <FadeUp className="lg:col-span-7">
                        <p className="eyebrow">About GrowVise</p>
                        <h1 className="mt-6 font-serif font-semibold text-white tracking-tight leading-[1.15] text-4xl sm:text-5xl max-w-xl">
                            Built by an operator, not a theorist.
                        </h1>
                        <p className="mt-6 text-base sm:text-lg text-slate-300/90 leading-relaxed max-w-xl">
                            GrowVise exists because too many good NZ product businesses stall on execution — not ideas. We bring the commercial judgment, structure and accountability to turn growth plans into results.
                        </p>
                        <div className="mt-10 grid grid-cols-2 gap-x-8 gap-y-6 max-w-md" data-testid="about-stats">
                            {STATS.map((s) => (
                                <div key={s.label} className="border-l-2 border-gold-500/60 pl-4">
                                    <p className={`font-serif font-semibold text-white ${s.value.length > 12 ? "text-base leading-snug" : "text-2xl"}`}>{s.value}</p>
                                    <p className="text-[11px] font-mono tracking-widest uppercase text-slate-500 mt-1">{s.label}</p>
                                </div>
                            ))}
                        </div>
                    </FadeUp>
                    <FadeUp delay={0.15} className="lg:col-span-5">
                        {FOUNDER.photo ? (
                            <figure className="bg-navy-900 border border-white/10 rounded-xl overflow-hidden" data-testid="founder-photo-panel">
                                <img src={FOUNDER.photo} alt={`${FOUNDER.name}, ${FOUNDER.role} of GrowVise`} className="w-full aspect-[4/5] object-cover object-top" />
                                <figcaption className="flex items-center justify-between gap-4 px-6 py-5 border-t border-white/10">
                                    <div>
                                        <p className="font-serif text-lg text-white" data-testid="founder-name">{FOUNDER.name}</p>
                                        <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-gold-400 mt-1">{FOUNDER.role}</p>
                                    </div>
                                    {FOUNDER.linkedin && (
                                        <a href={FOUNDER.linkedin} target="_blank" rel="noopener noreferrer" aria-label={`${FOUNDER.name} on LinkedIn`} className="text-slate-300 hover:text-gold-400 transition-colors duration-200" data-testid="founder-linkedin">
                                            <Linkedin size={22} />
                                        </a>
                                    )}
                                </figcaption>
                            </figure>
                        ) : (
                            <div className="bg-navy-900 border border-white/10 rounded-xl aspect-[4/3] flex flex-col items-center justify-center gap-6" data-testid="founder-logo-panel">
                                <LogoMark size={84} />
                                <div className="text-center">
                                    <p className="font-serif font-semibold text-2xl text-white">
                                        Grow<span className="text-gold-500">Vise</span>
                                    </p>
                                    <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500 mt-2">Commercial Growth &amp; Implementation</p>
                                </div>
                                <div className="text-center border-t border-white/10 pt-5 px-10">
                                    <p className="font-serif text-lg text-white" data-testid="founder-name">{FOUNDER.name}</p>
                                    <p className="font-mono text-[11px] tracking-[0.22em] uppercase text-gold-400 mt-1">{FOUNDER.role}</p>
                                    {FOUNDER.linkedin && (
                                        <a href={FOUNDER.linkedin} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm text-slate-300 hover:text-gold-400 transition-colors duration-200" data-testid="founder-linkedin">
                                            <Linkedin size={16} /> LinkedIn
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}
                    </FadeUp>
                </div>
            </section>

            {/* Story — cream, editorial measure */}
            <section className="section-pad bg-cream" data-testid="about-story">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10">
                    <FadeUp className="lg:col-span-4">
                        <p className="eyebrow-light">The story</p>
                    </FadeUp>
                    <FadeUp delay={0.05} className="lg:col-span-7 lg:col-start-6">
                        <div className="space-y-6 text-ink/85 leading-relaxed text-base sm:text-lg">
                            <p>
                                Over 16-plus years working in and around New Zealand product businesses — bringing more than 20 global brands to the NZ market, negotiating with retailers and distributors, importing product and building sales channels — one pattern kept repeating: the businesses that grew weren't the ones with the best ideas. They were the ones that executed commercially, week after week.
                            </p>
                            <p>
                                GrowVise was built around that insight. Not as an HR consultancy, and not as a coaching franchise — but as a commercial growth partner for product businesses that need sharper pricing, stronger channels, tighter margins and a team that follows through.
                            </p>
                            <p>
                                People and performance work is part of every engagement — but it's always in service of the commercial outcome: grow sales, improve margins, execute better, and reduce dependence on the owner.
                            </p>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* Values — white hairline grid */}
            <section className="section-pad bg-white border-t border-navy-900/10" data-testid="about-values">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <FadeUp className="max-w-xl">
                        <p className="eyebrow-light">How we work</p>
                        <h2 className="mt-5 font-serif font-semibold text-ink tracking-tight leading-snug text-3xl sm:text-4xl">
                            Principles behind every engagement.
                        </h2>
                    </FadeUp>
                    <Stagger className="mt-14 grid sm:grid-cols-2 gap-x-12">
                        {VALUES.map((v) => (
                            <StaggerItem key={v.title}>
                                <div className="py-7 border-t border-navy-900/10" data-testid={`value-card-${v.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                    <div className="flex items-center gap-3">
                                        <v.icon size={18} className="text-gold-700 shrink-0" />
                                        <h3 className="font-sans font-semibold text-ink">{v.title}</h3>
                                    </div>
                                    <p className="text-sm text-warmgrey leading-relaxed mt-3">{v.desc}</p>
                                </div>
                            </StaggerItem>
                        ))}
                    </Stagger>
                    <FadeUp className="mt-14">
                        <div className="bg-mist border-l-2 border-gold-500 rounded-r-lg p-7 sm:p-9" data-testid="about-ai-band">
                            <p className="text-ink/85 leading-relaxed max-w-3xl">
                                <span className="font-serif font-semibold text-ink">Commercial experience, accelerated by AI. </span>
                                AI helps us analyse faster, draft better and track actions relentlessly. You're paying for commercial judgment, prioritisation, relationships and accountability — the things software can't do.
                            </p>
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* CTA — navy band */}
            <section className="bg-navy-900 border-t border-white/10" data-testid="about-cta">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20 flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-16">
                    <FadeUp className="flex-1">
                        <h2 className="font-serif font-semibold text-white tracking-tight leading-snug text-3xl sm:text-4xl">
                            Let's talk about your business.
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.1} className="flex flex-wrap gap-4 shrink-0">
                        <Link to="/book-a-conversation" className="btn-gold" data-testid="about-book-cta">
                            Book a Growth Conversation
                        </Link>
                        <Link to="/business-health-check" className="btn-outline" data-testid="about-assessment-cta">
                            Free Business Health Check
                        </Link>
                    </FadeUp>
                </div>
            </section>
        </div>
    );
}
